import { useState } from 'react';

import {
  RiChat3Line,
  RiThumbDownFill,
  RiThumbDownLine,
  RiThumbUpFill,
  RiThumbUpLine,
} from '@remixicon/react';

import { ButtonIcon } from '@backstage/ui';

import { ChatEventFragment } from '@spotify/confidence-graphql';

import { useChatContext } from '../ChatThread/ChatContext';
import { type Sentiment, buildFeedbackMailto } from './buildFeedbackMailto';

const findPrecedingUserMessage = (
  events: ChatEventFragment[],
  eventName: string,
): string | undefined => {
  const eventIndex = events.findIndex(e => e.name === eventName);
  if (eventIndex === -1) return undefined;

  for (let i = eventIndex - 1; i >= 0; i--) {
    const msg = events[i].userMessage;
    if (msg) {
      const textFromParts = (msg.parts ?? [])
        .map(p => p.text)
        .filter((t): t is string => !!t)
        .join('\n\n');
      return textFromParts || msg.content || undefined;
    }
  }
  return undefined;
};

const openMailto = (url: string) => {
  window.location.href = url;
};

interface FeedbackToolbarProps {
  eventName: string;
  content: string;
}

export const FeedbackToolbar = ({
  eventName,
  content,
}: FeedbackToolbarProps) => {
  const chatContext = useChatContext();
  const [selectedSentiment, setSelectedSentiment] = useState<
    'positive' | 'negative' | null
  >(null);

  const handleFeedback = (sentiment: Sentiment) => {
    const userMessage = chatContext
      ? findPrecedingUserMessage(chatContext.chatEvents, eventName)
      : undefined;

    const url = buildFeedbackMailto({
      sentiment,
      chatName: chatContext?.chatName ?? '',
      eventName,
      assistantMessage: content,
      userMessage,
      chatUrl: window.location.href,
    });

    openMailto(url);

    if (sentiment === 'positive' || sentiment === 'negative') {
      setSelectedSentiment(prev => (prev === sentiment ? null : sentiment));
    }
  };

  return (
    <>
      <ButtonIcon
        variant="tertiary"
        size="small"
        icon={
          selectedSentiment === 'positive' ? (
            <RiThumbUpFill size={14} />
          ) : (
            <RiThumbUpLine size={14} />
          )
        }
        onPress={() => handleFeedback('positive')}
        aria-label="Positive feedback"
      />
      <ButtonIcon
        variant="tertiary"
        size="small"
        icon={
          selectedSentiment === 'negative' ? (
            <RiThumbDownFill size={14} />
          ) : (
            <RiThumbDownLine size={14} />
          )
        }
        onPress={() => handleFeedback('negative')}
        aria-label="Negative feedback"
      />
      <ButtonIcon
        variant="tertiary"
        size="small"
        icon={<RiChat3Line size={14} />}
        onPress={() => handleFeedback('comment')}
        aria-label="Leave a comment"
      />
    </>
  );
};
